import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useModalQueue } from './ModalQueueContext';
import { paymentStore } from '@/stores/paymentStore';
import { detectInAppBrowser } from '@/lib/in-app-browser';
import { isInAppBrowserOverlayDismissed } from '@/components/ProtectedRoute';
import { storage } from '@/lib/storage';
import { useUserInitQuery } from '@/hooks/queries/useUserInitQuery';

// Check if GeoComply should be bypassed
const shouldSkipGeoComply = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (storage.getItem('SKIP_GEOCOMPLY') === 'true') return true;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

interface JoiningBonusContextType {
  showJoiningBonusModal: boolean;
  setShowJoiningBonusModal: (show: boolean) => void;
  dismissJoiningBonusModal: () => void;
  triggerJoiningBonusModal: () => void;
  checkAndShowJoiningBonus: () => void;
}

const JoiningBonusContext = createContext<JoiningBonusContextType | undefined>(undefined);

export const useJoiningBonus = () => {
  const context = useContext(JoiningBonusContext);
  if (context === undefined) {
    throw new Error('useJoiningBonus must be used within a JoiningBonusProvider');
  }
  return context;
};

export const JoiningBonusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session, isGeoComplyCompliant } = useAuth();
  const { requestShow, notifyDismissed, shouldShow } = useModalQueue();
  const [hasChecked, setHasChecked] = useState(false);
  const [isEligible, setIsEligible] = useState(false);

  // Use unified init query for initial data (no separate API call needed)
  const { data: initData } = useUserInitQuery();

  // Check if we're in an in-app browser
  const isInAppBrowser = useMemo(() => detectInAppBrowser().isInAppBrowser, []);

  // Track if the in-app browser overlay has been dismissed
  const [inAppOverlayDismissed, setInAppOverlayDismissed] = useState(() => {
    return !isInAppBrowser || isInAppBrowserOverlayDismissed();
  });

  // Listen for in-app browser overlay dismissal event
  useEffect(() => {
    if (!isInAppBrowser) return;

    const handleDismissal = () => {
      setInAppOverlayDismissed(true);
    };

    window.addEventListener('inAppBrowserOverlayDismissed', handleDismissal);
    return () => window.removeEventListener('inAppBrowserOverlayDismissed', handleDismissal);
  }, [isInAppBrowser]);

  const checkAndShowJoiningBonus = useCallback(() => {
    if (!user || !session) return;
    if (isInAppBrowser && !inAppOverlayDismissed) return;
    if (!user.email_verified) return;
    if (!initData?.joiningBonus) return;

    const status = initData.joiningBonus.status;
    if (!status?.bonus_claimed) {
      setIsEligible(true);
      if (!paymentStore.getState().isOpen) {
        requestShow('JOINING_BONUS');
      }
    }
  }, [user, session, isInAppBrowser, inAppOverlayDismissed, requestShow, initData]);

  // Dismiss the modal
  const dismissJoiningBonusModal = useCallback(() => {
    setIsEligible(false);
    notifyDismissed('JOINING_BONUS');
  }, [notifyDismissed]);

  // Trigger the modal to show (for manual triggers from other components)
  const triggerJoiningBonusModal = useCallback(() => {
    setIsEligible(true);
    requestShow('JOINING_BONUS');
  }, [requestShow]);

  // Check joining bonus status when user logs in, geolocation passes, AND init data is loaded
  useEffect(() => {
    const canShow = !isInAppBrowser || inAppOverlayDismissed;
    const geoComplyPassed = shouldSkipGeoComply() || isGeoComplyCompliant === true;

    if (user && session && user.email_verified && geoComplyPassed && !hasChecked && canShow && initData?.joiningBonus) {
      checkAndShowJoiningBonus();
      setHasChecked(true);
    } else if (!user) {
      // Reset when user logs out
      setHasChecked(false);
      setIsEligible(false);
    } else if (user && session && (!user.email_verified || (!shouldSkipGeoComply() && isGeoComplyCompliant === false))) {
      setHasChecked(false);
      setIsEligible(false);
    }
  }, [user, session, isGeoComplyCompliant, hasChecked, isInAppBrowser, inAppOverlayDismissed, checkAndShowJoiningBonus, initData]);
  
  // Subscribe to payment store - request show when payment closes if eligible
  useEffect(() => {
    let wasOpen = paymentStore.getState().isOpen;
    
    const unsubscribe = paymentStore.subscribe(() => {
      const isOpen = paymentStore.getState().isOpen;
      
      // Payment just closed and we're eligible
      if (wasOpen && !isOpen && isEligible) {
        requestShow('JOINING_BONUS');
      }
      
      wasOpen = isOpen;
    });
    
    return unsubscribe;
  }, [isEligible, requestShow]);

  // Get visibility from queue
  const showJoiningBonusModal = shouldShow('JOINING_BONUS');

  const value = {
    showJoiningBonusModal,
    setShowJoiningBonusModal: triggerJoiningBonusModal, // Redirects to queue system
    dismissJoiningBonusModal,
    triggerJoiningBonusModal,
    checkAndShowJoiningBonus,
  };

  return (
    <JoiningBonusContext.Provider value={value}>
      {children}
    </JoiningBonusContext.Provider>
  );
};
